import { ModalNL } from "@components/modal/Modal"
import { useGetOpModesListQuery } from "@queries/useGetOpModesListQuery"
import * as Accordion from "@radix-ui/react-accordion"
import * as Checkbox from "@radix-ui/react-checkbox"
import { useAppSelector } from "@redux/app/hooks"
import { getUserData } from "@redux/features/userData/userDataSlice"
import { useQueryClient } from "@tanstack/react-query"
import { Button, IconButton, LoadingCircle } from "@ui-lib/index"
import { requestApi2 } from "@utils/http"
import { portalSocket } from "@utils/websocket/portalSocket"
import { seempSocket } from "@utils/websocket/seempSocket"
import { endpoints, SeempActionGroup } from "api"
import { Dispatch, FormEvent, SetStateAction, useState } from "react"
import { useTranslation } from "react-i18next"
import {
	MdChevronLeft,
	MdInfoOutline,
	MdOutlineUnfoldLess,
	MdOutlineUnfoldMore,
} from "react-icons/md"
import { getHandlePlantCacheUpdated } from "./SeempContent"

import MultiSelect from "@pages/DatabasePage/MultiSelect"
import { ValueLabelPair } from "@utilityTypes/ValueLabelPair"
import { common } from "@utils/common"
import { useSeempGetUsersQuery } from "./queries/useSeempGetUsersQuery"
import SeempInfoCard from "./SeempInfoCard"

export default function SeempNewReportBody({
	companyName,
	plantId,
	lastActiveTab,
	setActiveTab,
	actionGroups,
}: {
	companyName: string
	plantId: number
	lastActiveTab: string | null
	setActiveTab: Dispatch<SetStateAction<string | undefined>>
	actionGroups: SeempActionGroup[]
}) {
	const { t } = useTranslation()
	const tSeemp = useTranslation("seemp").t
	const queryClient = useQueryClient()
	const handlePlantCacheUpdated = getHandlePlantCacheUpdated(queryClient)
	const userData = useAppSelector(getUserData)

	const plantName = common.plantDataFromOwnerAndId.get(
		`${companyName.toLowerCase()}/${plantId}`
	)?.display_name

	const [reportName, setReportName] = useState<string>(`Seemp Report ${plantName}`)
	const [reportYear, setReportYear] = useState<number | undefined>()

	const {
		isLoading: isUsersListLoading,
		isError: isUsersListError,
		data: usersList,
	} = useSeempGetUsersQuery({ companyName })

	const usersOptions = usersList?.map((user) => ({
		value: user.email,
		label: `${user.name} (${user.email})`,
	}))

	const {
		isLoading: isOpModesListLoading,
		isError: isOpModesListError,
		data: opModesList,
	} = useGetOpModesListQuery({ plantIdsList: [plantId] })
	const [selectedItems, setSelectedItems] = useState<string[]>([])

	const [isCreateReportFormOpen, setIsCreateReportFormOpen] = useState<boolean>(false)
	const [isInfoCardOpen, setIsInfoCardOpen] = useState<boolean>(false)

	const handleActionCheckboxChange = (itemId: string) => {
		setSelectedItems((prev) =>
			prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
		)
	}

	const handleModeCheckboxChange = (itemId: string[]) => {
		setSelectedItems((prev) =>
			itemId.every((item) => selectedItems.includes(item)) === true
				? prev.filter((id) => itemId.includes(id) === false)
				: itemId.every((item) => selectedItems.includes(item)) === true
					? [...prev.filter((id) => itemId.includes(id) === false), ...itemId]
					: [...prev, ...itemId]
		)
	}

	const [responsibleUsers, setResponsibleUsers] = useState<ValueLabelPair[]>([])

	const handleCreateReport = async (
		plantId: number,
		name: string,
		year: number,
		userEmail: string,
		responsibleUsers: string[],
		actions: { actionDefinitionId: number; actionType: string }[]
	) => {
		if (
			reportName === "" ||
			reportYear === undefined ||
			reportYear < new Date().getFullYear()
		) {
			return
		}
		setIsCreateReportFormOpen(true)

		const newReportData = await requestApi2(endpoints.seempAddReport, {
			plantId,
			name,
			year,
			userEmail,
			responsibleUsers,
			actions,
			socketId: portalSocket.getSocketId(),
		})
		if (newReportData == null) {
			alert(t("Failed to create report. Please try again later."))
			setReportName("Seemp Report")
			setReportYear(new Date().getFullYear() + 1)
			setIsCreateReportFormOpen(false)
			return
		}

		setReportName("Seemp Report")
		setReportYear(new Date().getFullYear() + 1)
		seempSocket.sendInvalidatePlantCacheRequest(companyName, plantId)
		seempSocket.sendPlantCacheUpdateRequest(
			companyName,
			plantId,
			"add",
			newReportData,
			newReportData.id
		)
		handlePlantCacheUpdated({
			plantId,
			updateType: "add",
			data: newReportData,
			reportId: newReportData.id,
		})
		setIsCreateReportFormOpen(false)
		setActiveTab(`${newReportData.id}`)
	}

	// Expand all accordion items by default (only those with at least one action definition)
	const [openItems, setOpenItems] = useState<string[]>(() =>
		actionGroups.filter((ag) => ag.actionsDefinitions.length > 0).map((ag) => ag.label)
	)

	const handleUnfoldAll = () => {
		setOpenItems((prev) =>
			prev.length === actionGroups?.map((item) => item.label).length
				? []
				: actionGroups?.map((item) => item.label)
		)
	}

	const allActionsList = actionGroups?.flatMap((item) =>
		item.actionsDefinitions.map((action) => `${action.id}_${action.type}`)
	)

	return (
		<div
			className={`shadow-card flex h-fit max-h-[calc(100vh-3.25rem-3rem)] flex-col gap-3 overflow-auto rounded-sm border-0 bg-white p-4`}
		>
			<p
				className={
					"text-dipai-secondary-901 text-menu h-8r flex items-center font-semibold"
				}
			>
				{t("CREATE NEW SEEMP REPORT")}
				<IconButton
					variant="text-dark"
					onClick={() => setIsInfoCardOpen(true)}
				>
					<IconButton.Icon>{MdInfoOutline}</IconButton.Icon>
				</IconButton>
			</p>

			<div className={`mb-2 flex w-full flex-col justify-between gap-3`}>
				<div className={`flex flex-row gap-3`}>
					<div className={`flex w-[calc(100%-5rem)] flex-col gap-1.5`}>
						<label
							htmlFor="report-name"
							className={`text-tiny h-4`}
						>
							{t("REPORT NAME")}
						</label>
						<input
							id="report-name"
							onChange={(e: FormEvent<HTMLInputElement>) => {
								const target = e.target as HTMLInputElement
								setReportName(target.value)
							}}
							value={reportName}
							className={`border-dipai-secondary-901/20 text-tiny hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 h-8 rounded-sm border-1 p-2 ring-1 ring-white/0 outline-0`}
						/>
					</div>
					<div className={`flex w-20 flex-col gap-1.5`}>
						<label
							htmlFor="report-year"
							className={`text-tiny h-4`}
						>
							{t("TARGET YEAR")}
						</label>
						<input
							id="report-year"
							type="number"
							min={new Date().getFullYear()}
							max={new Date().getFullYear() + 3}
							onChange={(e) => {
								setReportYear(Number(e.target.value))
								if (
									reportYear !== undefined &&
									reportName.includes(reportYear.toString())
								) {
									setReportName(
										reportName.replace(reportYear.toString(), e.target.value)
									)
								}
							}}
							value={reportYear}
							className={`border-dipai-secondary-901/20 text-tiny hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 h-8 rounded-sm border-1 p-2`}
						/>
					</div>
					<div className={`flex w-200 flex-col gap-1.5`}>
						<label
							htmlFor="responsible-persons"
							className={`text-tiny h-4`}
						>
							{t("RESPONSIBLE PERSONS")}
						</label>
						<MultiSelect
							options={usersOptions ?? []}
							value={responsibleUsers}
							onChange={(selectedOptions) => {
								setResponsibleUsers(selectedOptions as ValueLabelPair[])
							}}
							isClearable={true}
							placeHolderText={t("SELECT THE RESPONSIBLE PERSONS")}
							maxAmountItems={10}
						/>
					</div>
				</div>
			</div>
			{/* ACTIONS LIST */}
			<div className={`flex flex-col gap-1.5`}>
				<p className={`text-button h-6`}>{`${t("INITIAL ACTIONS")}`}</p>
				<div className={`flex flex-row justify-between gap-3 pr-1.5 pl-[13.5px]`}>
					<div className={`flex items-center gap-2`}>
						{/* TODO: ADD TOOL TIP FOR ALL BUTTOM */}
						<Checkbox.Root
							className="border-dipai-secondary-901/20 hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 flex size-6 items-center justify-center rounded-sm border-1 bg-white ring-1 ring-white/0 outline-0"
							checked={
								allActionsList.length === selectedItems.length
									? true
									: selectedItems.length > 0
										? "indeterminate"
										: false
							}
							onCheckedChange={() =>
								allActionsList.length === selectedItems.length
									? setSelectedItems([])
									: setSelectedItems(allActionsList)
							}
						>
							<Checkbox.Indicator className="text-dipai-secondary-901">
								{allActionsList.length === selectedItems.length ? (
									<CheckIcon />
								) : selectedItems.length > 0 ? (
									<DividerHorizontalIcon />
								) : null}
							</Checkbox.Indicator>
						</Checkbox.Root>
						<p className={`text-button h-6`}>
							<span
								className={`px-1 font-semibold`}
							>{`${selectedItems.length}/${allActionsList.length}`}</span>
							{`${t("ACTIONS SELECTED").toLowerCase()}`}
						</p>
					</div>
					<div className={`flex gap-2`}>
						<IconButton
							variant={"text-dark"}
							onClick={handleUnfoldAll}
						>
							{openItems.length === actionGroups.map((item) => item.label).length ? (
								<IconButton.Icon>{MdOutlineUnfoldLess}</IconButton.Icon>
							) : (
								<IconButton.Icon>{MdOutlineUnfoldMore}</IconButton.Icon>
							)}
						</IconButton>
					</div>
				</div>
				<div
					className={`max-h-[calc(100vh-3.25rem-2.5rem-12rem-5rem)] overflow-auto rounded-sm border-1`}
				>
					<Accordion.Root
						className="flex w-full flex-col"
						type="multiple"
						value={openItems}
						onValueChange={setOpenItems}
					>
						{opModesList !== null && opModesList !== undefined
							? actionGroups
									.filter((ag) => ag.actionsDefinitions.length > 0)
									.map((item) => (
										<Accordion.Item
											key={item.label}
											value={item.label}
											className={`border-dipai-secondary-901/20 overflow-hidden rounded-t-sm border-1 not-last:border-b-0`}
										>
											<Accordion.Header
												className={`hover:bg-dipai-secondary-800/5 data-[state=open]:bg-dipai-secondary-800/5 grid h-11 grid-cols-[1.5rem_1fr] gap-3 p-3 py-2.5`}
											>
												<Checkbox.Root
													className="border-dipai-secondary-901/20 hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 flex size-6 items-center justify-center rounded-sm border-1 bg-white ring-1 ring-white/0 outline-0"
													checked={
														item.actionsDefinitions
															.map(
																(action) =>
																	`${action.id}_${action.type}`
															)
															.every((item) =>
																selectedItems.includes(item)
															) === true
															? true
															: item.actionsDefinitions
																		.map(
																			(action) =>
																				`${action.id}_${action.type}`
																		)
																		.some((item) =>
																			selectedItems.includes(
																				item
																			)
																		) === true
																? "indeterminate"
																: false
													}
													onCheckedChange={() =>
														handleModeCheckboxChange(
															item.actionsDefinitions.map(
																(action) =>
																	`${action.id}_${action.type}`
															)
														)
													}
													id={`${item.label}`}
													onClick={(e) => e.stopPropagation()}
												>
													<Checkbox.Indicator className="text-dipai-secondary-901">
														{item.actionsDefinitions
															.map(
																(action) =>
																	`${action.id}_${action.type}`
															)
															.every((item) =>
																selectedItems.includes(item)
															) === true ? (
															<CheckIcon />
														) : item.actionsDefinitions
																.map(
																	(action) =>
																		`${action.id}_${action.type}`
																)
																.some((item) =>
																	selectedItems.includes(item)
																) === true ? (
															<DividerHorizontalIcon />
														) : null}
													</Checkbox.Indicator>
												</Checkbox.Root>
												<Accordion.Trigger
													className={
														"group grid w-full grid-cols-[1fr_1.25rem] gap-2 outline-none"
													}
												>
													<label className="text-dipai-secondary-901 text-button pl-0 text-left font-semibold hover:cursor-pointer">
														{item.label}
													</label>
													<div
														className={`flex items-center justify-center`}
													>
														<MdChevronLeft
															className="text-dipai-secondary-901 group-hover:text-dipai-secondary-600 h-5 w-5 rotate-180 transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-hover:scale-110 group-hover:cursor-pointer group-data-[state=open]:rotate-270"
															aria-hidden
														/>
													</div>
												</Accordion.Trigger>
											</Accordion.Header>
											<Accordion.Content
												className={
													"data-[state=closed]:animate-slide-accordion-up data-[state=open]:animate-slide-accordion-down grid w-full gap-2.5 overflow-hidden p-3"
												}
											>
												<p className={`text-button`}>
													{tSeemp(`${item.label}.${item.description}`)}
												</p>
												{item.actionsDefinitions.map((action) => (
													<div
														key={action.type}
														className="flex items-center"
													>
														<Checkbox.Root
															className="border-dipai-secondary-901/20 hover:border-dipai-secondary-500 focus:ring-dipai-secondary-500 flex size-6 items-center justify-center rounded-sm border-1 bg-white ring-1 ring-white/0 outline-0"
															checked={selectedItems.includes(
																`${action.id}_${action.type}`
															)}
															onCheckedChange={() =>
																handleActionCheckboxChange(
																	`${action.id}_${action.type}`
																)
															}
															id={`${action.id}_${action.type}`}
														>
															<Checkbox.Indicator className="text-dipai-secondary-901">
																<CheckIcon />
															</Checkbox.Indicator>
														</Checkbox.Root>
														<label
															className="text-tiny pl-3"
															htmlFor={`${action.id}_${action.type}`}
														>
															<p
																className={`text-dipai-secondary-901 h-4 font-semibold first-letter:uppercase`}
															>
																{action.type.includes(":")
																	? tSeemp(
																			`${item.label}.${action.type
																				.split(":")
																				.slice(1)
																				.join(":")
																				.trimStart()}`
																		)
																	: t(action.type)}
															</p>
															<p
																className={`text-dipai-secondary-901/80 h-4 font-normal`}
															>
																{tSeemp(
																	`${item.label}.${action.description}`
																)}
															</p>
														</label>
													</div>
												))}
											</Accordion.Content>
										</Accordion.Item>
									))
							: null}
					</Accordion.Root>
				</div>
			</div>
			{/* BUTTONS */}
			<div className={`flex flex-row justify-end gap-3`}>
				<Button
					variant={"outline-dark"}
					onClick={() => lastActiveTab !== null && setActiveTab(lastActiveTab)}
				>
					<Button.Label>{t("CANCEL")}</Button.Label>
				</Button>
				<Button
					variant={"filled-secondary"}
					disabled={
						reportName === "" ||
						reportYear === undefined ||
						reportYear < new Date().getFullYear() ||
						isCreateReportFormOpen
					}
					onClick={() => {
						if (
							opModesList === undefined ||
							opModesList === null ||
							reportYear === undefined
						) {
							alert(t("No operation modes available. Please try again later."))
							return
						}
						handleCreateReport(
							plantId,
							reportName,
							reportYear,
							userData.email,
							responsibleUsers.map((user) => user.value),
							selectedItems
								.map((item) => {
									const [actionDefinitionId, actionType] = item.split("_") as [
										string,
										string,
									]
									return {
										actionDefinitionId: Number(actionDefinitionId),
										actionType,
									}
								})
								.filter(
									(
										item
									): item is { actionDefinitionId: number; actionType: string } =>
										isNaN(item.actionDefinitionId) === false
								)
						)
					}}
				>
					<Button.Label>{t("CREATE")}</Button.Label>
				</Button>

				<ModalNL
					// initialFocus={multiSelectRef}
					isOpen={isCreateReportFormOpen}
					setIsOpen={setIsCreateReportFormOpen}
					opacity={0.75}
				>
					<SeempCreateReportBody />
				</ModalNL>
				<ModalNL
					isOpen={isInfoCardOpen}
					setIsOpen={setIsInfoCardOpen}
					opacity={0.75}
				>
					<SeempInfoCard setModalOpenState={setIsInfoCardOpen} />
				</ModalNL>
			</div>
		</div>
	)
}

function SeempCreateReportBody() {
	const { t } = useTranslation()

	return (
		<div
			className={`shadow-card fixed inset-1/2 flex h-80 w-80 -translate-1/2 flex-col items-center justify-around rounded-lg bg-white`}
		>
			<div className={`relative flex h-45 w-full items-center justify-center`}>
				<div className={`absolute w-full animate-pulse`} />
				<LoadingCircle
					size="medium"
					containerHeightWithUnit="180px"
				/>
			</div>
			<label className={`text-menu h-8 text-center font-semibold`}>
				{t("CREATING REPORT, PLEASE WAIT...")}
			</label>

			<button
				tabIndex={0}
				style={{ opacity: 0, position: "absolute", pointerEvents: "none" }}
				aria-hidden="true"
			>
				Focusable
			</button>
		</div>
	)
}

export const CheckIcon = () => {
	return (
		<svg
			width="15"
			height="15"
			viewBox="0 0 15 15"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M11.4669 3.72684C11.7558 3.91574 11.8369 4.30308 11.648 4.59198L7.39799 11.092C7.29783 11.2452 7.13556 11.3467 6.95402 11.3699C6.77247 11.3931 6.58989 11.3355 6.45446 11.2124L3.70446 8.71241C3.44905 8.48022 3.43023 8.08494 3.66242 7.82953C3.89461 7.57412 4.28989 7.55529 4.5453 7.78749L6.75292 9.79441L10.6018 3.90792C10.7907 3.61902 11.178 3.53795 11.4669 3.72684Z"
				fill="currentColor"
				fillRule="evenodd"
				clipRule="evenodd"
			/>
		</svg>
	)
}

const DividerHorizontalIcon = () => {
	return (
		<svg
			width="15"
			height="15"
			viewBox="0 0 15 15"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M2 7.5C2 7.22386 2.22386 7 2.5 7H12.5C12.7761 7 13 7.22386 13 7.5C13 7.77614 12.7761 8 12.5 8H2.5C2.22386 8 2 7.77614 2 7.5Z"
				fill="currentColor"
				fillRule="evenodd"
				clipRule="evenodd"
			/>
		</svg>
	)
}
